import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner, Modal, Form, Button, Table } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles, TestimonialModalStyles } from './styles/adminTestimonials.js';
import { useAuth } from '../../context/AuthContext';
import {
    ADMIN_TESTIMONIALS_ENDPOINT,
    ADMIN_TESTIMONIALS_UPDATE_ENDPOINT,
    ADMIN_GALLERY_ENDPOINT,
    API_BASE_URL
} from '../../config/api';

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' }
];

const initialForm = {
    authorName: '',
    authorTitle: '',
    quote: '',
    avatarUrl: '',
    status: 'draft'
};

const AdminTestimonials = () => {
    const { user, loading: authLoading } = useAuth();

    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalItems: 0,
        totalPages: 1
    });
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [modalState, setModalState] = useState({
        show: false,
        mode: 'add',
        testimonial: null,
        form: initialForm,
        saving: false,
        error: ''
    });
    const [deletingId, setDeletingId] = useState('');
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [galleryError, setGalleryError] = useState('');
    const [gallerySelection, setGallerySelection] = useState('');

    const isAdmin = useMemo(() => {
        if (!Array.isArray(user?.roles)) {
            return false;
        }
        return user.roles.some((role) => typeof role === 'string' && role.toLowerCase() === 'admin');
    }, [user]);

    const fetchTestimonials = useCallback(async () => {
        if (!isAdmin) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(pageSize)
            });
            if (searchTerm.trim()) {
                params.set('search', searchTerm.trim());
            }
            if (statusFilter) {
                params.set('status', statusFilter);
            }

            const response = await fetch(`${ADMIN_TESTIMONIALS_ENDPOINT}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load testimonials.');
            }

            const payload = await response.json();
            setTestimonials(Array.isArray(payload.testimonials) ? payload.testimonials : []);
            if (payload.pagination) {
                const serverPage = payload.pagination.page || page;
                setPagination({
                    page: serverPage,
                    limit: payload.pagination.limit || pageSize,
                    totalItems: payload.pagination.totalItems || 0,
                    totalPages: payload.pagination.totalPages || 1
                });
                if (serverPage !== page) {
                    setPage(serverPage);
                }
            } else {
                setPagination({
                    page,
                    limit: pageSize,
                    totalItems: payload.testimonials?.length || 0,
                    totalPages: 1
                });
            }
        } catch (err) {
            setError(err.message || 'Unable to load testimonials.');
            setTestimonials([]);
        } finally {
            setLoading(false);
        }
    }, [isAdmin, page, pageSize, searchTerm, statusFilter]);

    useEffect(() => {
        if (!authLoading && isAdmin) {
            fetchTestimonials();
        }
    }, [authLoading, isAdmin, fetchTestimonials]);

    const loadGalleryImages = useCallback(async () => {
        if (!isAdmin) {
            return;
        }

        setGalleryLoading(true);
        setGalleryError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load gallery images.');
            }

            const payload = await response.json();
            setGalleryImages(Array.isArray(payload.images) ? payload.images : []);
        } catch (err) {
            setGalleryError(err.message || 'Unable to load gallery images.');
            setGalleryImages([]);
        } finally {
            setGalleryLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (modalState.show) {
            loadGalleryImages();
        }
    }, [modalState.show, loadGalleryImages]);

    useEffect(() => {
        if (!modalState.show) {
            return;
        }
        const current = modalState.form.avatarUrl;
        if (!current) {
            setGallerySelection('');
            return;
        }

        const match = galleryImages.some((image) => {
            const absolute = image.url && image.url.startsWith('http')
                ? image.url
                : `${API_BASE_URL}${image.url || ''}`;
            return absolute === current;
        });

        setGallerySelection(match ? current : '');
    }, [modalState.show, modalState.form.avatarUrl, galleryImages]);

    const tableRows = useMemo(() => testimonials.map((testimonial) => ({
        id: testimonial._id,
        authorName: testimonial.authorName || 'Unknown',
        authorTitle: testimonial.authorTitle || '',
        quote: testimonial.quote || '',
        status: testimonial.status ? testimonial.status.charAt(0).toUpperCase() + testimonial.status.slice(1) : 'Draft',
        avatarUrl: testimonial.avatarUrl || '',
        updatedAt: testimonial.updatedAt ? new Date(testimonial.updatedAt).toLocaleString() : 'N/A',
        raw: testimonial
    })), [testimonials]);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setSearchTerm(searchInput.trim());
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        if (searchTerm) {
            setSearchTerm('');
        }
    };

    const openModal = (mode, testimonial = null) => {
        if (mode === 'edit' && testimonial) {
            setModalState({
                show: true,
                mode,
                testimonial,
                form: {
                    authorName: testimonial.authorName || '',
                    authorTitle: testimonial.authorTitle || '',
                    quote: testimonial.quote || '',
                    avatarUrl: testimonial.avatarUrl || '',
                    status: testimonial.status || 'draft'
                },
                saving: false,
                error: ''
            });
            setGallerySelection(testimonial.avatarUrl || '');
        } else {
            setModalState({
                show: true,
                mode: 'add',
                testimonial: null,
                form: initialForm,
                saving: false,
                error: ''
            });
            setGallerySelection('');
        }
    };

    const closeModal = () => {
        setModalState({
            show: false,
            mode: 'add',
            testimonial: null,
            form: initialForm,
            saving: false,
            error: ''
        });
        setGallerySelection('');
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setModalState((prev) => ({
            ...prev,
            form: {
                ...prev.form,
                [name]: value
            },
            error: ''
        }));
        if (name === 'avatarUrl') {
            setGallerySelection('');
        }
    };

    const handleGallerySelect = (event) => {
        const selected = event.target.value;
        setGallerySelection(selected);
        if (!selected) {
            return;
        }
        setModalState((prev) => ({
            ...prev,
            form: {
                ...prev.form,
                avatarUrl: selected
            },
            error: ''
        }));
    };

    const submitModal = async (event) => {
        event.preventDefault();
        setModalState((prev) => ({ ...prev, saving: true, error: '' }));

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            if (!modalState.form.authorName.trim()) {
                throw new Error('Author name is required.');
            }
            if (!modalState.form.quote.trim()) {
                throw new Error('Testimonial quote is required.');
            }

            const payload = {
                authorName: modalState.form.authorName.trim(),
                authorTitle: modalState.form.authorTitle.trim(),
                quote: modalState.form.quote.trim(),
                avatarUrl: modalState.form.avatarUrl.trim(),
                status: modalState.form.status
            };

            const requestOptions = {
                method: modalState.mode === 'add' ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            };

            const endpoint = modalState.mode === 'add'
                ? ADMIN_TESTIMONIALS_ENDPOINT
                : ADMIN_TESTIMONIALS_UPDATE_ENDPOINT(modalState.testimonial._id);

            const response = await fetch(endpoint, requestOptions);

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.message || 'Unable to save testimonial.');
            }

            await response.json();
            closeModal();
            fetchTestimonials();
        } catch (err) {
            setModalState((prev) => ({ ...prev, error: err.message || 'Unable to save testimonial.' }));
        } finally {
            setModalState((prev) => ({ ...prev, saving: false }));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this testimonial? This action cannot be undone.')) {
            return;
        }

        setDeletingId(id);
        setError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_TESTIMONIALS_UPDATE_ENDPOINT(id), {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to delete testimonial.');
            }

            fetchTestimonials();
        } catch (err) {
            setError(err.message || 'Unable to delete testimonial.');
        } finally {
            setDeletingId('');
        }
    };

    return (
        <Styles>
            <TestimonialModalStyles />
            <div className="main-wrapper admin-testimonials-page">
                <HeaderTwo />
                <BreadcrumbBox title="Manage Testimonials" />

                <section className="testimonials-area">
                    <Container>
                        <Row>
                            <Col md="12">
                                {!authLoading && !isAdmin && (
                                    <Alert variant="danger">You do not have permission to view this page.</Alert>
                                )}
                                {isAdmin && (
                                    <div className="card-box">
                                        <div className="actions">
                                            <Button onClick={() => openModal('add')}>Add Testimonial</Button>
                                            <form onSubmit={handleSearchSubmit} className="search-form">
                                                <input
                                                    type="text"
                                                    placeholder="Search by author or quote"
                                                    value={searchInput}
                                                    onChange={(event) => setSearchInput(event.target.value)}
                                                    disabled={loading}
                                                />
                                                <button type="submit" disabled={loading}>Search</button>
                                                <button
                                                    type="button"
                                                    className="clear-btn"
                                                    onClick={handleClearSearch}
                                                    disabled={loading || (!searchTerm && !searchInput.trim())}
                                                >
                                                    Clear
                                                </button>
                                            </form>
                                            <div className="filter-controls">
                                                <label htmlFor="testimonial_status_filter">Status</label>
                                                <select
                                                    id="testimonial_status_filter"
                                                    value={statusFilter}
                                                    onChange={(event) => {
                                                        setStatusFilter(event.target.value);
                                                        setPage(1);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <option value="">All</option>
                                                    {STATUS_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                                        {loading ? (
                                            <div className="text-center my-4">
                                                <Spinner animation="border" role="status" />
                                            </div>
                                        ) : (
                                            <div className="data-grid">
                                                {tableRows.length === 0 ? (
                                                    <Alert variant="info" className="mb-0">No testimonials found.</Alert>
                                                ) : (
                                                    <>
                                                        <Table responsive bordered hover>
                                                            <thead>
                                                                <tr>
                                                                    <th>Author</th>
                                                                    <th>Quote</th>
                                                                    <th>Status</th>
                                                                    <th>Updated</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tableRows.map((row) => {
                                                                    const quotePreview = row.quote.length > 120
                                                                        ? `${row.quote.slice(0, 117)}…`
                                                                        : row.quote;
                                                                    const avatar = row.avatarUrl || '';
                                                                    return (
                                                                        <tr key={row.id}>
                                                                            <td>
                                                                                <div className="author-cell">
                                                                                    {avatar ? (
                                                                                        <img src={avatar} alt={row.authorName} />
                                                                                    ) : (
                                                                                        <div className="placeholder-avatar">{row.authorName.charAt(0).toUpperCase()}</div>
                                                                                    )}
                                                                                    <div>
                                                                                        <strong>{row.authorName}</strong>
                                                                                        {row.authorTitle && (
                                                                                            <span className="muted">{row.authorTitle}</span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td>{quotePreview}</td>
                                                                            <td className="text-capitalize">{row.status}</td>
                                                                            <td>{row.updatedAt}</td>
                                                                            <td className="actions-cell">
                                                                                <button
                                                                                    type="button"
                                                                                    className="link-button"
                                                                                    onClick={() => openModal('edit', row.raw)}
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                                <span className="divider">|</span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="link-button delete"
                                                                                    onClick={() => handleDelete(row.id)}
                                                                                    disabled={deletingId === row.id}
                                                                                >
                                                                                    {deletingId === row.id ? 'Deleting…' : 'Delete'}
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                        <div className="pagination-controls">
                                                            <div className="page-info">
                                                                Page {pagination.page} of {pagination.totalPages} · {pagination.totalItems} total
                                                            </div>
                                                            <div className="page-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                                                    disabled={pagination.page <= 1 || loading}
                                                                >
                                                                    Previous
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                                                                    disabled={pagination.page >= pagination.totalPages || loading}
                                                                >
                                                                    Next
                                                                </button>
                                                                <select
                                                                    value={pageSize}
                                                                    onChange={(event) => {
                                                                        const newSize = Number(event.target.value) || 10;
                                                                        setPageSize(newSize);
                                                                        setPage(1);
                                                                    }}
                                                                    disabled={loading}
                                                                >
                                                                    {[10, 20, 50, 100].map((size) => (
                                                                        <option key={size} value={size}>
                                                                            {size} / page
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>

            <Modal
                show={modalState.show}
                onHide={closeModal}
                centered
                dialogClassName="admin-testimonials-modal"
                size="lg"
            >
                <Form onSubmit={submitModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{modalState.mode === 'add' ? 'Add Testimonial' : 'Edit Testimonial'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalState.error && <Alert variant="danger">{modalState.error}</Alert>}
                        <Row>
                            <Col md="6">
                                <Form.Group controlId="testimonial_author">
                                    <Form.Label>Author Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="authorName"
                                        value={modalState.form.authorName}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="testimonial_title">
                                    <Form.Label>Author Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="authorTitle"
                                        value={modalState.form.authorTitle}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Student, Engineer, etc."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="testimonial_quote">
                                    <Form.Label>Quote</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="quote"
                                        value={modalState.form.quote}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        required
                                        placeholder="What did they say?"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="6">
                                <Form.Group controlId="testimonial_avatar">
                                    <Form.Label>Avatar URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="avatarUrl"
                                        value={modalState.form.avatarUrl}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="https://example.com/avatar.jpg"
                                    />
                                    <div className="gallery-picker">
                                        <span className="muted">
                                            Paste an external URL or choose an uploaded gallery image.
                                        </span>
                                        {galleryLoading ? (
                                            <div className="gallery-status">
                                                <Spinner animation="border" size="sm" role="status" />
                                                <span>Loading gallery…</span>
                                            </div>
                                        ) : (
                                            <>
                                                <Form.Control
                                                    as="select"
                                                    value={gallerySelection}
                                                    onChange={handleGallerySelect}
                                                    disabled={modalState.saving || galleryImages.length === 0}
                                                >
                                                    <option value="">Select from gallery…</option>
                                                    {galleryImages.map((image) => {
                                                        const absolute = image.url && image.url.startsWith('http')
                                                            ? image.url
                                                            : `${API_BASE_URL}${image.url || ''}`;
                                                        return (
                                                            <option key={image.fileName} value={absolute}>
                                                                {image.fileName}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Control>
                                                {galleryImages.length === 0 && !galleryError && (
                                                    <span className="muted">No gallery images available yet.</span>
                                                )}
                                            </>
                                        )}
                                        {galleryError && (
                                            <span className="text-danger small">{galleryError}</span>
                                        )}
                                    </div>
                                    {modalState.form.avatarUrl && (
                                        <div className="avatar-preview">
                                            <span className="preview-label">Preview</span>
                                            <img
                                                src={modalState.form.avatarUrl}
                                                alt="Avatar preview"
                                                className="img-fluid"
                                            />
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="testimonial_status">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={modalState.form.status}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={closeModal} disabled={modalState.saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={modalState.saving}>
                            {modalState.saving ? 'Saving...' : 'Save Testimonial'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Styles>
    );
};

export default AdminTestimonials;
